import { createContext, useContext, useState } from 'react'
import { getApi } from './api-common'
import { TAddon, TDuration, TPlanSummary } from './cms-types'
import { planToEvent } from './utils/planToEvent'
import { addonToEvent, directorToEvent } from './utils/addonToEvent'
import {
  AddonSlug,
  additionalPlans,
  addonToProductMapping,
  PlanSlug,
  planToParent,
} from './planSlugs'
import { AddressType } from './types/address-type'
import { Director } from './types/director-type'
import { Project } from './project'
import {
  TAdditionalService,
  TCheckoutResponse,
  TOrder,
  TRequire3ds,
} from './types/order-type'
import { getPriceIds } from 'components/util/getPriceIds'
import { coupon } from './useCalculatePrice'
import { BusinessType } from './types/business-type'
import { Utils } from './utils'
import dataRelay from 'data-relay'
import Cookies from 'js-cookie'
import { getUtmParam } from 'project/useUtmTracking'
import {
  getOrderStorageKey,
  getTenantHeaders,
  getTenantId,
  Tenant,
} from './tenant'
import { reportPaymentError } from './utils/reportPaymentError'

// Get tenant-specific storage key for orders
// UK uses 'order' for backwards compatibility, AU uses 'order_2'

export type DirectorsState = {
  company_number: string
  officers: Director[]
  selected: Director[]
}

export type BagItem = {
  slug: PlanSlug | null
  duration: TDuration
  addons: AddonSlug[]
  addonSources: Record<Partial<AddonSlug>, string>
  additionalPlans: PlanSlug[]
  directors?: DirectorsState
}

export type CheckoutAPIType = {
  reference_id?: string
  billing_request_id?: string
  setup_intent?: string
  customer_id?: string
  first_name: string
  last_name: string
  phone_number: string
  payment_type: string
  customer_email: string
  business: {
    abn_number?: string | null
    acn_number?: string | null
    business_type: string
    legal_name: string
    country_of_corporation: string
    trading_name: string | null
    company_number: string | null
    description: string | null
    sic_codes?: string[] | null
  }
  billing_address: AddressType
  address: AddressType
  product_id: string
  addons?: { id: string; quantity?: number }[]
  additionalPlans?: string[]
  directors?: string[]
}

export type CheckoutPriceData = {
  planPrice: number
  addonPrice: number
  vatPrice: number
  totalPrice: number
  discount: number | null
  discountDescription: string
  cardBrand?: string
  cardLast4?: string
}

export type Require3dsType = (
  setup_intent: string,
  client_secret: string,
  customer_id: string,
) => Promise<{
  setup_intent?: string
  client_secret?: string
  customer_id: string
  /** Stripe's error message when 3DS confirmation failed, for diagnostics */
  error_message?: string
}>
export const getIsMailPlan = (plan: TPlanSummary | undefined) =>
  plan?.slug === PlanSlug.SCAN_PRO || plan?.slug === PlanSlug.SCAN_LITE
export const trackService = (
  plan: string,
  service: string,
  postcheckout: boolean,
  url?: string,
) => {
  if (Project.dataRelay) {
    try {
      dataRelay.sendEvent(
        {
          event: postcheckout
            ? 'Post-checkout Business Service'
            : 'Business Service',
          location: document.location.href,
          plan: plan,
          sku: service,
          source: url,
        },
        { apiKey: Project.dataRelay },
      )
    } catch (e) {}
  }
}

export const trackOrder = (order: TOrder) => {
  if (Project.dataRelay) {
    try {
      if (order.additional_services?.length) {
        dataRelay.sendEvent(
          {
            event: 'Checkout with Services',
            location: document.location.href,
            services: order.additional_services?.map(
              (v) => v.partner_offer_product,
            ),
            sku: order.plan,
          },
          { apiKey: Project.dataRelay },
        )
      }
      dataRelay.sendEvent(
        {
          additional_plans: order.additional_plans?.join(',') || '',
          addons: order.addons,
          email: order.contact_details?.email,
          event: 'Sale',
          propertyString1: order.payment_method?.payment_type,
          propertyString2: order.period,
          propertyString3: '1',
          services: order.additional_services?.map(
            (v) => v.partner_offer_product,
          ),
          sku: order.plan,
          source: 'Website',
          total:
            order.plan.toLowerCase().includes('annual') ||
            order.plan.toLowerCase().includes('yearly')
              ? order.total / 12
              : order.total,
        },
        { apiKey: Project.dataRelay },
      )
    } catch (e) {}
  }
}

export const ShoppingBagContext = createContext({
  setValue: (value: BagItem) => {
    // console.error('Tried to set bag', value)
  },
  value: {
    additionalPlans: [],
    addonSources: {},
    addons: [],
    duration: 'month',
    slug: null,
  } as BagItem,
})

export default function () {
  const { setValue, value } = useContext(ShoppingBagContext)

  const [addonDetails, setAddonDetails] = useState<TAddon[]>([])

  const removePlan = (plan: TPlanSummary, plans?: TPlanSummary[]) => {
    getApi().trackEvent(
      'remove_from_cart_ga4',
      planToEvent(plan, value.duration),
    )

    const additionalPlans = value.additionalPlans || []
    const currentPlanParent = value.slug
      ? planToParent[value.slug as PlanSlug]
      : null

    // Check if it's an additional plan
    if (additionalPlans.includes(plan.slug as PlanSlug)) {
      setValue({
        additionalPlans: additionalPlans.filter((slug) => slug !== plan.slug),
        addonSources: value.addonSources || {},
        addons: value.addons,
        directors: value.directors,
        duration: value.duration,
        slug: value.slug,
      })
    } else if (value.slug === plan.slug) {
      // Removing the current plan directly (e.g., removing SCAN_PRO when it's in basket)

      // Check if the current plan has a parent plan (e.g., SCAN_PRO -> SCAN_LITE)
      if (currentPlanParent && plans) {
        const parentPlanObj = plans.find((p) => p.slug === currentPlanParent)
        if (parentPlanObj) {
          // Replace with parent plan instead of removing
          setValue({
            additionalPlans: additionalPlans,
            addonSources: value.addonSources || {},
            addons: value.addons,
            directors: value.directors,
            duration: value.duration,
            slug: currentPlanParent,
          })
          return
        }
      }

      // Removing the main plan completely
      setValue({
        additionalPlans: additionalPlans,
        addonSources: value.addonSources || {},
        addons: value.addons || [],
        directors: value.directors,
        duration: 'month',
        slug: null,
      })
    } else if (currentPlanParent === plan.slug) {
      // Removing the parent of the current plan (e.g., removing SCAN_LITE when basket has SCAN_PRO)
      // Remove the child plan completely
      setValue({
        additionalPlans: additionalPlans,
        addonSources: value.addonSources || {},
        addons: value.addons || [],
        directors: value.directors,
        duration: 'month',
        slug: null,
      })
    }
  }

  const clearBasket = () => {
    setValue({
      additionalPlans: [],
      addonSources: {},
      addons: [],
      directors: undefined,
      duration: 'month',
      slug: null,
    })
  }
  const removePlanAddon = (addonSlug: string, data: TAddon) => {
    const newPlanAddons = addonDetails?.filter((v) => v.slug !== addonSlug)
    setAddonDetails(newPlanAddons)
    Utils.setProperties({
      checkout_offers: newPlanAddons?.map((v) => v.offer_category).join(','),
    })
    if (
      value.addons.find((v) => {
        return v === addonSlug
      })
    ) {
      getApi().trackEvent(
        'remove_from_cart_ga4',
        addonToEvent(data, value.duration),
      )
      setValue({
        ...value,
        addons: value.addons.filter((v) => {
          return v !== addonSlug
        }),
      })
    }
  }

  const addPlan = (
    slug: PlanSlug,
    duration: TDuration = 'month',
    summary: TPlanSummary,
    buttonType?: string,
  ) => {
    const currentAdditionalPlans = value.additionalPlans || []

    // do nothing if plan is already in bag
    if (value.slug === slug || currentAdditionalPlans.includes(slug)) {
      return
    }

    // @ts-ignore
    getApi().trackEvent(
      'add_to_cart_ga4',
      planToEvent(summary, duration, buttonType),
    )

    // Check if the plan is an additional plan
    if (additionalPlans.includes(slug)) {
      // If there's a main plan, add to additionalPlans array
      if (value.slug) {
        setValue({
          additionalPlans: [...currentAdditionalPlans, slug],
          addonSources: value.addonSources || {},
          addons: value.addons,
          directors: value.directors,
          duration: value.duration,
          slug: value.slug,
        })
      } else {
        // No main plan, add to additionalPlans array
        setValue({
          additionalPlans: [...currentAdditionalPlans, slug],
          addonSources: value.addonSources || {},
          addons: value.addons,
          directors: value.directors,
          duration,
          slug: null,
        })
      }
    } else if (
      // Attempting to add a plan that could also be an addon to a non-empty basket
      addonToProductMapping[slug] &&
      value.slug
    ) {
      //Only add the addon if it doesn't exist
      if (!value.addons.includes(addonToProductMapping[slug]!)) {
        setValue({
          additionalPlans: currentAdditionalPlans,
          addonSources: value.addonSources || {},
          addons: value.addons.concat([addonToProductMapping[slug]!]),
          directors: value.directors,
          duration: value.duration,
          slug: value.slug,
        })
      }
    } else {
      setValue({
        additionalPlans: currentAdditionalPlans,
        addonSources: value.addonSources || {},
        addons:
          // @ts-ignore
          value.slug &&
          addonToProductMapping[value.slug] &&
          !value.addons.includes(addonToProductMapping[value.slug]!)
            ? // Attempting to add a plan to a non-empty basket that has a product which could be an addon
              value.addons.concat([addonToProductMapping[value.slug]!])
            : value.addons,
        directors: value.directors,
        duration,
        slug,
      })
    }
  }

  const replacePlan = (
    slug: PlanSlug,
    duration: TDuration = 'month',
    summary: TPlanSummary,
  ) => {
    setValue({
      additionalPlans: [],
      addonSources: {},
      addons: [],
      duration,
      slug: slug,
    })
  }

  const checkout = async (
    data: CheckoutAPIType,
    plans: TPlanSummary[],
    addons: TAddon[],
    additional_services: string[],
    onRequire3ds: Require3dsType,
    priceData: CheckoutPriceData,
  ) => {
    // If no main plan but there are additional plans, use the first additional plan as main
    const additionalPlans = value.additionalPlans || []
    const currentPlan =
      value.slug || (additionalPlans.length > 0 ? additionalPlans[0] : null)
    const currentAddons = value.addons
    const currentAdditionalPlans = value.slug
      ? additionalPlans
      : additionalPlans.slice(1)

    const directorsCount = value.directors?.selected?.length || 0
    const ids = getPriceIds(
      plans,
      addons,
      currentPlan,
      currentAddons,
      value.duration,
      currentAdditionalPlans,
      directorsCount,
    )
    if (!ids?.product_id) {
      return Promise.reject('You have no products')
    }
    // Attribution forwarded to the hoxtonmix-api checkout handler, which stores
    // it on the Chargebee customer's metadata (customer.meta_data.attribution).
    // See project/checkout-attribution.ts. Lazy-imported so the 21KB module
    // stays out of the _app chunk (this hook is app-wide via context).
    const { buildCheckoutAttribution } = await import(
      'project/checkout-attribution'
    )
    const attribution = await buildCheckoutAttribution(data.customer_email)

    const body = {
      ...data,
      ...attribution,
      additional_plans: ids.additionalPlans?.filter((v) => !!v),
      additional_services,
      addons: ids.addons?.filter((v) => !!v),
      business: {
        ...data.business,
        legal_name:
          data.business.business_type === BusinessType.SOLE_TRADER
            ? data.business.trading_name
            : data.business.legal_name,
      },
      directors: value.directors?.selected?.length
        ? value.directors.selected.map((d) => d.external_id)
        : undefined,
      google_clid: Cookies.get('_gcl_aw'),
      payment_type: coupon?.discountedTotal === 0 ? 'NONE' : data.payment_type,
      product_id: ids.product_id,
      reference_id: data.reference_id,
      referrer: getUtmParam('referrer') || undefined,
      tolt_referral: window.tolt_referral || undefined,
      // utm_campaign carries only the campaign — utm_content is sent separately
      // via the spread `...attribution` above (buildCheckoutAttribution →
      // customer.cf_utm_content). Concatenating the two here polluted the
      // subscription's cf_utm_campaign with "campaign_content" strings.
      utm_campaign: getUtmParam('utm_campaign') || undefined,
      utm_medium: getUtmParam('utm_medium'),
      // Final fallback when no UTM, click-ID, or referrer was captured (typed
      // URL / bookmark / referrer stripped) = genuinely direct. Was 'website',
      // a placeholder that blended true-direct with untagged traffic; the
      // upstream click-ID + referrer synthesis in useUtmTracking now resolves
      // real origins, so anything left here is Direct. (content-os classifier
      // still special-cases the legacy 'website' value for historical rows.)
      utm_source: getUtmParam('utm_source') || 'direct',
      voucher: coupon?.code,
    } as CheckoutAPIType

    // Build the order from frontend data - only order_id and customer_id come from the API
    const buildOrder = (apiRes: TCheckoutResponse): TOrder => {
      const serviceAddons = currentAddons
        .filter((v) =>
          addons.find((addon) => v === addon.slug && !!addon.serviceLink),
        )
        .filter((v) => !!v)
        .map((v) => addons.find((addon) => v === addon.slug))
        .map((v) => ({
          link_url: v?.serviceLink || '',
          partner_offer_product: v?.slug || ('' as any),
        }))

      const apiServiceAddons = currentAddons
        .filter((v) =>
          addons.find(
            (addon) =>
              v === addon.slug && !!addon.isService && !addon.serviceLink,
          ),
        )
        .map((v) => {
          const apiService = apiRes.additional_services?.find(
            (s) => s.partner_offer_product === v,
          )
          return {
            link_url: apiService?.link_url || '',
            partner_offer_product: v as any,
          }
        })

      // Build additional_services: API response is source of truth, fill gaps from client
      const allServices: TAdditionalService[] = [
        ...(apiRes.additional_services || []),
      ]
      const hasService = (slug: string) =>
        allServices.some((s) => s.partner_offer_product === slug)

      for (const s of [...apiServiceAddons, ...serviceAddons]) {
        if (!hasService(s.partner_offer_product)) {
          allServices.push(s)
        }
      }

      return {
        additional_plans: ids.additionalPlans?.filter((v) => !!v) || [],
        additional_services: allServices,
        addons: [
          ...currentAddons.filter(
            (v) => !addons.find((addon) => v === addon.slug && addon.isService),
          ),
          ...(value.directors?.selected?.length
            ? [AddonSlug.DIRECTORS_SERVICE_ADDRESS]
            : []),
        ],
        company_details: {
          company_name: body.business.legal_name || body.business.trading_name,
          company_number: data.business.company_number,
          company_phone_number: '',
        },
        contact_details: {
          email: data.customer_email,
          first_name: data.first_name,
          last_name: data.last_name,
          phone_number: data.phone_number,
        },
        customer_id: apiRes.customer_id,
        directors: value.directors?.selected?.length
          ? value.directors.selected
          : undefined,
        discount_amount: priceData.discount,
        order_id: apiRes.order_id,
        payment_method: {
          card_type: (priceData.cardBrand ||
            'unknown') as TOrder['payment_method']['card_type'],
          last4: priceData.cardLast4
            ? parseInt(priceData.cardLast4, 10)
            : undefined,
          payment_type: (coupon?.discountedTotal === 0
            ? 'NONE'
            : data.payment_type) as any,
        },
        period: value.duration,
        plan: ids.product_id,
        plan_amount: priceData.planPrice,
        sub_total: apiRes.sub_total,
        tax: apiRes.tax,
        tax_description: getTenantId() === Tenant.AU ? 'GST' : 'VAT',
        total: apiRes.total,
      }
    }

    return fetch(`${Project.api}checkout/`, {
      body: JSON.stringify(body),
      headers: getTenantHeaders(),
      method: 'POST',
    })
      .then(async (response) => {
        if (response.status >= 200 && response.status < 300) {
          return Promise.resolve(response)
        } else {
          const text = await response.text()
          reportPaymentError(
            'checkout',
            `checkout/ returned ${response.status}`,
            {
              body: text,
              email: body.customer_email,
              payment_type: body.payment_type,
              product_id: body.product_id,
              status: response.status,
            },
          )
          return Promise.reject(text)
        }
      })
      .then((res) => {
        return res.json()
      })
      .then(async (apiRes: TCheckoutResponse | TRequire3ds) => {
        let res = apiRes
        if ((res as TRequire3ds).setup_intent) {
          res = res as TRequire3ds
          const { client_secret, customer_id, error_message, setup_intent } =
            await onRequire3ds(
              res.setup_intent,
              res.client_secret,
              res.customer_id,
            )
          if (!setup_intent) {
            reportPaymentError(
              '3ds',
              error_message || 'Payment intent was not successful',
              {
                customer_id,
                email: body.customer_email,
                payment_type: body.payment_type,
                setup_intent: res.setup_intent,
              },
            )
            throw 'Payment intent was not successful'
          }
          res = await fetch(`${Project.api}checkout/confirm`, {
            body: JSON.stringify({
              ...body,
              client_secret,
              customer_id,
              setup_intent,
            }),
            headers: getTenantHeaders(),
            method: 'POST',
          })
            .then(async (response) => {
              if (response.status >= 200 && response.status < 300) {
                return Promise.resolve(response)
              } else {
                const text = await response.text()
                reportPaymentError(
                  'confirm',
                  `checkout/confirm returned ${response.status}`,
                  {
                    body: text,
                    customer_id,
                    email: body.customer_email,
                    payment_type: body.payment_type,
                    setup_intent,
                    status: response.status,
                  },
                )
                return Promise.reject(text)
              }
            })
            .then((res) => {
              return res.json() as Promise<TCheckoutResponse>
            })
        }
        res = res as TCheckoutResponse
        if (res.order_id) {
          const order = buildOrder(res)
          // Client-side GA4 purchase event removed 2026-06-12. The server-side
          // Chargebee → sGTM purchase (event_source=chargebee_webhook) is now the
          // single source of truth. Both used to fire with DIFFERENT
          // transaction_ids (client = order_id, server = invoice/subscription id),
          // so GA4 / Google Ads / Meta double-counted purchases & revenue. The
          // browser match keys (em/gclid/fbc/fbp) are already forwarded
          // server-side via the Chargebee customer attribution metadata, and the
          // server event fires on confirmed subscription_created (survives
          // ad-blockers / ITP / tab-close), so nothing is lost by removing this.
          order.additional_services?.forEach((service) => {
            trackService(
              order.plan,
              service?.partner_offer_product,
              false,
              value.addonSources?.[service.partner_offer_product],
            )
          })
          trackOrder(order)
          getApi().storage.setItemSync(
            getOrderStorageKey(),
            JSON.stringify(order),
          )
          return order
        } else {
          reportPaymentError('checkout', 'Order was not created', {
            email: body.customer_email,
            payment_type: body.payment_type,
            response: res,
          })
          throw 'Order was not created'
        }
      })
      .catch((e) => {
        throw e
      })
  }

  const updateDuration = (
    duration: TDuration = 'month',
    summary: TPlanSummary,
  ) => {
    // @ts-ignore
    getApi().trackEvent('add_to_cart_ga4', planToEvent(summary, duration))
    //todo: if existing basket or new product is virtual phone, replace with 020 add-on
    setValue({
      ...value,
      duration,
    })
  }

  const addPlanAddon = (
    addon: AddonSlug,
    data: TAddon,
    buttonType?: string,
  ) => {
    if (data.offer) {
      const newPlanAddons = addonDetails
        ?.filter((v) => v.slug !== addon)
        .concat([data])
      setAddonDetails(newPlanAddons)
      Utils.setProperties({
        checkout_offers: newPlanAddons?.map((v) => v.offer_category).join(','),
      })
    }
    if (value.addons.find((addonName) => addon === addonName)) {
      console.warn('Attempting to add already added add-on')
      return
    }

    getApi().trackEvent(
      'add_to_cart_ga4',
      addonToEvent(data, value.duration, buttonType),
    )

    setValue({
      ...value,
      addonSources: {
        ...(value.addonSources || {}),
        [addon]: document.location.pathname,
      },
      addons: value.addons.concat([addon]),
    })
  }

  const hasPlanInBag = (slug: string) => {
    return value.slug === slug
  }

  const parentPlan = value.slug ? planToParent[value.slug] : undefined
  let itemsInBag = value.slug ? 1 + value.addons.length : 0
  if (parentPlan) {
    itemsInBag = itemsInBag + 1
  }
  // Add additional plans to the count
  itemsInBag += (value.additionalPlans || []).length
  // Add selected directors to the count
  itemsInBag += value.directors?.selected?.length || 0

  // Compute the value to return
  // If no main plan but there are additional plans, use the first additional plan as the main
  const additionalPlansArray = value.additionalPlans || []
  const computedValue: BagItem =
    !value.slug && additionalPlansArray.length > 0
      ? {
          ...value,
          additionalPlans: additionalPlansArray.slice(1),
          slug: additionalPlansArray[0],
        }
      : { ...value, additionalPlans: additionalPlansArray }

  const isEmptyBasket = !value.slug && additionalPlansArray.length === 0

  const setDirectorOfficers = (
    officers: Director[],
    companyNumber: string,
    initialSelected?: Director[],
  ) => {
    const currentCompany = value.directors?.company_number
    const companyChanged = currentCompany && currentCompany !== companyNumber
    // Carry a selection over by Companies House id, so anyone no longer
    // in the refreshed list drops out of it
    const previouslySelected = value.directors?.selected || []
    const stillSelected = officers.filter((officer) =>
      previouslySelected.some((d) => d.external_id === officer.external_id),
    )
    setValue({
      ...value,
      // Remove DSA addon when company changes since directors are reset
      addons: companyChanged
        ? value.addons.filter((a) => a !== AddonSlug.DIRECTORS_SERVICE_ADDRESS)
        : value.addons,
      directors: {
        company_number: companyNumber,
        officers,
        selected: initialSelected
          ? [...initialSelected]
          : companyChanged
          ? []
          : stillSelected,
      },
    })
  }

  const addDirector = (director: Director, companyNumber: string) => {
    const current = value.directors || {
      company_number: companyNumber,
      officers: [],
      selected: [],
    }
    if (current.selected.find((d) => d.id === director.id)) return
    getApi().trackEvent('add_to_cart_ga4', directorToEvent(value.duration))
    setValue({
      ...value,
      directors: {
        ...current,
        company_number: companyNumber,
        selected: [...current.selected, director],
      },
    })
  }

  const removeDirector = (directorId: string) => {
    if (!value.directors) return
    getApi().trackEvent('remove_from_cart_ga4', directorToEvent(value.duration))
    setValue({
      ...value,
      directors: {
        ...value.directors,
        selected: value.directors.selected.filter((d) => d.id !== directorId),
      },
    })
  }

  const addAllDirectors = (
    directorsToSelect: Director[],
    companyNumber: string,
  ) => {
    const newCount = directorsToSelect.length
    if (newCount > 0) {
      getApi().trackEvent(
        'add_to_cart_ga4',
        directorToEvent(value.duration, undefined, newCount),
      )
    }
    setValue({
      ...value,
      directors: {
        company_number: companyNumber,
        officers: value.directors?.officers || directorsToSelect,
        selected: [...directorsToSelect],
      },
    })
  }

  const clearDirectors = () => {
    setValue({
      ...value,
      directors: undefined,
    })
  }

  const deselectAllDirectors = () => {
    if (!value.directors) return
    const removedCount = value.directors.selected.length
    if (removedCount > 0) {
      getApi().trackEvent(
        'remove_from_cart_ga4',
        directorToEvent(value.duration, undefined, removedCount),
      )
    }
    setValue({
      ...value,
      directors: {
        ...value.directors,
        selected: [],
      },
    })
  }

  const addService = (order: TOrder, service: TAddon) => {
    const body = {
      additional_service: service.slug,
      company_name: order.company_details.company_name,
      company_number: order.company_details.company_number,
      customer_email: order.contact_details.email,
      customer_id: order.customer_id,
      first_name: order.contact_details.first_name,
      last_name: order.contact_details.last_name,
      phone_number: order.contact_details.phone_number,
    }
    if (service.serviceLink) {
      return Promise.resolve({
        link_url: service.serviceLink,
        partner_offer_product: service.slug,
      })
    }
    return fetch(`${Project.api}offers/referrals`, {
      body: JSON.stringify(body),
      headers: getTenantHeaders(),
      method: 'POST',
    })
      .then((res) => res.json())
      .then((res) => {
        trackService(
          order?.plan?.toLowerCase(),
          service?.slug?.toLowerCase(),
          true,
        )
        return res as TAdditionalService | null
      })
  }

  return {
    addAllDirectors,
    addDirector,
    addPlan,
    addPlanAddon,
    addService,
    checkout,
    clearBasket,
    clearDirectors,
    deselectAllDirectors,
    hasPlanInBag,
    isEmptyBasket,
    itemsInBag,
    removeDirector,
    removePlan,
    removePlanAddon,
    replacePlan,
    setDirectorOfficers,
    updateDuration,
    value: computedValue,
  }
}
